// Lib imports
import _ from "lodash";
import Vue from "vue";
import VueRouter from "vue-router";

// App imports
import api from "@/api/cloud";
import store from "@/store";

// Routes
import isRouteForbidden from "./helpers/isRouteForbidden";
import patchRouter from "./monkey-patch";
import paths from "./paths";

// Grab environment query param, if present
let environment = new URL(location.href).searchParams.get("environment");

Vue.use(VueRouter);

const router = patchRouter(
	new VueRouter({
		mode: "history",
		base: process.env.BASE_URL,
		routes: paths,
	})
);

// Helper to automatically update active community selection
async function setActiveCommunity(to) {
	// Always make sure that communities are loaded
	await store.dispatch("communities/load");

	const communityId = _.toInteger(to.params.communityId);
	const activeCommunityId = store.state.session.communityId;

	if (communityId !== activeCommunityId) {
		store.commit("session/setCommunityId", communityId);

		// Community has changed: selected system is no longer valid
		if (store.state.session.systemId) {
			store.commit("session/setSystemId", null);
		}
	}
}

// Helper to automatically update active system selection
async function setActiveSystem(to) {
	// Always make sure that communities are loaded
	await store.dispatch("communities/load");

	// Get associated community for the given system
	const systemId = to.params.systemId;
	const groupId = await api.getSystemGroup(systemId).then(resp => resp.groupId);
	const community = store.getters["communities/itemByIdentifier"](groupId);

	// Set session's active community
	setActiveCommunity({ params: { communityId: community.id } });

	// Load systems for given community
	await store.dispatch("rooms/load", community);

	// Update session's active system
	const activeSystemId = store.state.session.systemId;

	if (systemId !== activeSystemId) {
		store.commit("session/setSystemId", systemId);
	}
}

async function loginRedirect(to, from, next) {
	const isLogin = to.matched.some(route => route.name === "Login");
	const isAuthed = store.getters["auth/signedIn"];
	if (!isLogin && !isAuthed) {
		try {
			await store.dispatch("auth/getCurrentUser");
		} catch (e) {
			next({
				name: "Login",
				query: { redirectFrom: to.fullPath },
			});
			return true;
		}
	}
}

async function syncVuexWithRoute(to, from, next) {
	try {
		if (to.params.systemId) await setActiveSystem(to);
		else if (to.params.communityId) await setActiveCommunity(to);
	} catch (e) {
		console.error("Failed!", e);
		next(e);
		return true;
	}
}

function checkRouteAuth(to, from, next) {
	let reason = isRouteForbidden(to, next);
	if (reason) {
		window.vm.$log(`Failed to navigate to (${to.path}): ${reason}`);

		// Perform some cleanup...
		store.commit("session/setCommunityId", null);
		store.commit("session/setSystemId", null);

		// Redirect to forbidden page.
		next({ name: "Forbidden" });
		return true;
	}
}

router.beforeEach(async (to, from, next) => {
	// If user specified an environment as a query param, make sure to preserve
	// that param. (This only matters when page is reloaded, like when using
	// live-reload during development.)
	if (environment && to.query.environment !== environment) {
		to.query.environment = environment;
		return next(to);
	}

	// If we're not on login page and not signed in, go to login page
	if (await loginRedirect(to, from, next)) return;

	next();
});

router.beforeResolve(async (to, from, next) => {
	// Automatically update *active* community and system from route params
	if (await syncVuexWithRoute(to, from, next)) return;

	// Make sure we have authorization to navigate to specified page
	if (checkRouteAuth(to, from, next)) return;

	next();
});

router.afterEach(async (to, from) => {
	// Dynamically set vuetify theme based on route
	const isDark = to.meta.theme === "dark";
	router.app.$vuetify.theme.dark = isDark;
});

export default router;
